// GEOFENCING / OPERATIONAL WATCH ZONES — draw zones on a tactical map,
// receive entry/exit/new-call alerts, acknowledge them.
import React, { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, Marker, Tooltip, useMapEvents } from "react-leaflet";
import L from "leaflet";
import client from "@/lib/api";
import { toast } from "sonner";
import { Hexagon, BellRing, CheckCircle2, Trash2 } from "lucide-react";
import { BASEMAPS } from "@/components/dispatchmap/mapShared";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const PURPOSES = ["perimeter", "school", "event", "hazard", "patrol", "restricted"];
const ALERT_KINDS = [["unit_entry", "UNIT ENTERS"], ["unit_exit", "UNIT EXITS"], ["incident_inside", "NEW CALL INSIDE"]];
const KIND_COLOR = { unit_entry: "#22C55E", unit_exit: "#F59E0B", incident_inside: "#EF4444" };

const pinIcon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9],
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#F59E0B;border:3px solid #fff;box-shadow:0 0 8px #F59E0B"></div>' });

function ClickCapture({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

export function WatchZones() {
  const [zones, setZones] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [pick, setPick] = useState(null);
  const [form, setForm] = useState({ name: "", purpose: "perimeter", radius_m: 400, alert_on: ["unit_entry", "incident_inside"] });
  const load = useCallback(() => {
    client.get("/command/zones").then((r) => setZones(r.data.zones || [])).catch(() => {});
    client.get("/command/zone-alerts", { params: { limit: 40 } }).then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const toggleKind = (k) => setForm((f) => ({ ...f, alert_on: f.alert_on.includes(k) ? f.alert_on.filter((x) => x !== k) : [...f.alert_on, k] }));
  const create = async () => {
    if (!form.name.trim()) { toast.error("Zone name required"); return; }
    if (!pick) { toast.error("Click the map to place the zone center first"); return; }
    try {
      await client.post("/command/zones", { ...form, lat: pick.lat, lng: pick.lng, radius_m: Number(form.radius_m) });
      toast.success(`Watch zone "${form.name}" activated`);
      setForm({ name: "", purpose: "perimeter", radius_m: 400, alert_on: ["unit_entry", "incident_inside"] });
      setPick(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create zone"); }
  };
  const deactivate = async (z) => {
    try { await client.patch(`/command/zones/${z.zone_id}`, { active: false }); toast(`Zone ${z.name} deactivated`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not deactivate"); }
  };
  const ack = async (a) => {
    try { await client.post(`/command/zone-alerts/${a.alert_id}/ack`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not acknowledge"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-3" data-testid="watch-zones">
      <div className="space-y-3">
        <div className="border border-[#2C2D35] rounded-sm overflow-hidden" style={{ height: 420 }} data-testid="zones-map">
          <MapContainer center={[40.4406, -79.9959]} zoom={12} style={{ height: "100%", width: "100%" }}>
            <TileLayer url={BASEMAPS.dark.url} attribution={BASEMAPS.dark.attr} />
            <ClickCapture onPick={setPick} />
            {pick && <Marker position={[pick.lat, pick.lng]} icon={pinIcon}><Tooltip permanent direction="top">NEW ZONE CENTER</Tooltip></Marker>}
            {pick && <Circle center={[pick.lat, pick.lng]} radius={Number(form.radius_m) || 400} pathOptions={{ color: "#F59E0B", dashArray: "6 6", fillOpacity: 0.1 }} />}
            {zones.map((z) => (
              <Circle key={z.zone_id} center={[z.center.lat, z.center.lng]} radius={z.radius_m}
                pathOptions={{ color: z.color || "#F59E0B", weight: 2, dashArray: "10 6", fillOpacity: 0.08 }}>
                <Tooltip direction="center" permanent><span style={{ fontFamily: "monospace", fontSize: 9, fontWeight: 700 }}>⬡ {z.name}</span></Tooltip>
              </Circle>
            ))}
          </MapContainer>
        </div>
        <div className="border border-[#2C2D35] rounded-sm p-3 space-y-2" data-testid="zone-create-form">
          <div className="font-head text-xs font-semibold flex items-center gap-2"><Hexagon size={13} className="text-[#F59E0B]" /> NEW WATCH ZONE — click map to place center</div>
          <div className="flex gap-2 flex-wrap items-center">
            <input data-testid="zone-name-input" className={`${inp} w-52`} placeholder="Zone name (e.g. Parade Perimeter)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select data-testid="zone-purpose-select" className={inp} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
              {PURPOSES.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
            </select>
            <label className="font-mono text-[10px] text-[#9CA3AF]">RADIUS (m)
              <input data-testid="zone-radius-input" type="number" min={30} max={50000} className={`${inp} w-24 ml-1.5`} value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} />
            </label>
            {ALERT_KINDS.map(([k, lab]) => (
              <button key={k} data-testid={`zone-alert-${k}`} onClick={() => toggleKind(k)}
                className="px-2 py-1 text-[9px] font-head font-bold rounded-full"
                style={form.alert_on.includes(k) ? { background: KIND_COLOR[k], color: "#0A0A0A" } : { border: "1px solid #2C2D35", color: "#6B7280" }}>
                {lab}
              </button>
            ))}
            <span className="font-mono text-[9px] text-[#6B7280]">{pick ? `CENTER ${pick.lat.toFixed(4)}, ${pick.lng.toFixed(4)}` : "NO CENTER PLACED"}</span>
            <button data-testid="zone-create-btn" onClick={create} className="px-4 py-2 rounded-sm font-head font-bold text-xs text-black" style={{ background: "#F59E0B" }}>ACTIVATE ZONE</button>
          </div>
        </div>
        <div className="border border-[#2C2D35] rounded-sm" data-testid="zones-list">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ACTIVE ZONES ({zones.length})</div>
          {zones.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No active watch zones.</div>}
          {zones.map((z) => (
            <div key={z.zone_id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] text-[11px] flex-wrap" data-testid={`zone-row-${z.zone_id}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: z.color || "#F59E0B" }} />
              <span className="text-white font-semibold">{z.name}</span>
              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded border border-[#2C2D35] text-[#9CA3AF]">{z.purpose.toUpperCase()}</span>
              <span className="font-mono text-[10px] text-[#9CA3AF]">{z.radius_m} m · alerts: {(z.alert_on || []).map((k) => k.replace(/_/g, " ")).join(", ") || "none"}</span>
              <span className="font-mono text-[9px] text-[#6B7280] flex-1">{z.created_by} · {(z.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <button data-testid={`zone-deactivate-${z.zone_id}`} onClick={() => deactivate(z)} className="text-[#EF4444] flex items-center gap-1 font-head text-[10px] font-bold"><Trash2 size={12} /> DEACTIVATE</button>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-[#2C2D35] rounded-sm h-fit" data-testid="zone-alerts-feed">
        <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
          <BellRing size={13} className="text-[#EF4444]" /> ZONE ALERTS <span className="text-[#6B7280] font-mono text-[10px]">({alerts.filter((a) => !a.ack).length} unacked)</span>
        </div>
        {alerts.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No zone alerts yet — alerts fire on unit entry/exit and new calls inside zones.</div>}
        <div className="max-h-[560px] overflow-y-auto">
          {alerts.map((a) => (
            <div key={a.alert_id} className="px-3 py-2 border-b border-[#1E1F24]" style={{ opacity: a.ack ? 0.55 : 1 }} data-testid={`zone-alert-${a.alert_id}`}>
              <div className="flex items-center gap-2">
                <span className="font-head text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: KIND_COLOR[a.kind] || "#6B7280", color: "#0A0A0A" }}>{(a.kind || "").replace(/_/g, " ").toUpperCase()}</span>
                <span className="font-mono text-[9px] text-[#6B7280]">{(a.ts || "").slice(5, 16).replace("T", " ")}</span>
                {!a.ack ? (
                  <button data-testid={`zone-alert-ack-${a.alert_id}`} onClick={() => ack(a)} className="ml-auto text-[#22C55E] flex items-center gap-1 font-head text-[10px] font-bold"><CheckCircle2 size={12} /> ACK</button>
                ) : <span className="ml-auto font-mono text-[9px] text-[#22C55E]">✓ {a.ack_by}</span>}
              </div>
              <div className="font-mono text-[10px] text-white mt-1">{a.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
